import React from 'react';

import PropTypes from 'prop-types';

import { useDevice } from '@bonnet/next/device';

import { Glyphicon, Heading, Image, Link, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxPrivateSellerExchangeLogoClick } from 'reaxl-analytics-handlers';
import { useFeatures } from 'reaxl-features';

import AtcBranding from '@/components/AtcBranding';

import FilterLocationModalContainer from './FilterLocationModalContainer';
import SRPBreadcrumbsContainer from './SRPBreadcrumbsContainer';

function SrpHeroContainer({
    backToResultsPath,
    locationHeading,
    savedSearchTitle,
    sellerType,
}) {
    const {
        // feature configs
        brand: [, {
            atc_branding: showAtcBranding,
        }],
        enable_private_seller_exchange_logo: [enablePrivateSellerExchangeLogo],
        srp_hero: [, {
            logo: srpHeroLogo,
            alt_text: altText,
            breadcrumbs: showBreadcrumbs = true,
            secondary_link_text: secondaryLinkText,
            link_text: linkText,
            sub_text: subText,
        }],
        personalization: [isPersonalizationEnabled] = [],
        psx_logo: [, {
            icon: privateSellerIcon,
            href: privateSellerExchangeLogoHref,
            width: privateSellerIconWidth,
        }],
    } = useFeatures([
        'brand',
        'enable_private_seller_exchange_logo',
        'srp_hero',
        'personalization',
        'psx_logo',
    ]);

    const device = useDevice();
    const isXs = device?.is?.xs ?? false;

    const handlePrivateSellerExchangeLogoClick = (e) => {
        sendClick(psxPrivateSellerExchangeLogoClick, e);
    };

    const onlyPrivateSellerSelected = !Array.isArray(sellerType) && sellerType === 'p';

    const containerStyle = isXs ? { minHeight: 'calc((var(--sem-type-heading-4) * 1.2) * 2)' } : undefined;

    return (
        <>
            {isPersonalizationEnabled && savedSearchTitle && (
                <Text
                    color="text-subdued"
                    data-cmp="saved-search-title"
                >
                    {savedSearchTitle}
                </Text>
            )}
            {enablePrivateSellerExchangeLogo && onlyPrivateSellerSelected && (
                <Link
                    target="_blank"
                    href={privateSellerExchangeLogoHref}
                    onClick={handlePrivateSellerExchangeLogoClick}
                >
                    <Image
                        src={privateSellerIcon}
                        alt="Private Seller Exchange"
                        height={27}
                        width={privateSellerIconWidth}
                        lazyload={false}
                        loading="eager"
                    />
                </Link>
            )}
            {(showBreadcrumbs || showAtcBranding) && (
                <div className="margin-bottom-2 display-flex justify-content-between align-items-baseline">
                    {showBreadcrumbs && (<SRPBreadcrumbsContainer />)}
                    {showAtcBranding && (<AtcBranding className="text-right flex-1" />)}
                </div>
            )}

            {linkText && (
                <div className="margin-bottom-2 text-size-200">
                    <Glyphicon
                        className="margin-right-2"
                        glyph="chevron-left"
                    />
                    <Link
                        href={backToResultsPath ?? 'cars-for-sale/all-cars'}
                        undecorated
                    >
                        {linkText}
                    </Link>
                </div>
            )}

            <div style={containerStyle}>
                <Heading
                    componentClass="h1"
                    className="display-inline margin-right-2"
                    data-cmp="srp-title"
                    level={4}
                    id="search-results-heading"
                >
                    {locationHeading}
                </Heading>

                <FilterLocationModalContainer className="text-link link-undecorated" />

                {secondaryLinkText && !isXs && (
                    <span className="pull-right">
                        <Text
                            size={100}
                            className="margin-right-2"
                        >
                            {secondaryLinkText}
                        </Text>
                        <Image
                            src={srpHeroLogo}
                            alt={altText}
                            lazyload={false}
                            loading="eager"
                            width={120}
                        />
                    </span>
                )}
            </div>

            {subText && (
                <Text
                    componentClass="div"
                    color="accent"
                    weight="bold"
                    className="margin-right-auto"
                >
                    {subText}
                </Text>
            )}
        </>
    );
}

SrpHeroContainer.propTypes = {
    /**
     * Optional string for the back-to-results link
     */
    backToResultsPath: PropTypes.string,
    /**
     * Required string for the location heading
     */
    locationHeading: PropTypes.string.isRequired,
    /**
     * Optional string for the saved search title
     */
    savedSearchTitle: PropTypes.string,
    /**
     * String or an array of the seller types
     */
    sellerType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default SrpHeroContainer;
